import { type FlagDto, raiseFlag, updateFlag } from "@repo/data-store";
import { asPatientId } from "@repo/domain";
import { FLAG_CATEGORY_SYSTEM } from "@repo/fhir-systems";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { useSubmitOnce } from "@/shared/forms/submit-once";

const CATEGORY_OPTIONS = [
  { value: "safety", label: "Safety" },
  { value: "clinical", label: "Clinical" },
  { value: "administrative", label: "Administrative" },
  { value: "behavioral", label: "Behavioral" },
  { value: "contact", label: "Contact" },
] as const;

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(CATEGORY_OPTIONS.map((o) => [o.value, o.label]));

const flagFormSchema = z.object({
  label: z.string().trim().min(1, "Enter an alert"),
  category: z.string().min(1, "Pick a category"),
});

function categoryConcept(code: string) {
  return [{ coding: [{ system: FLAG_CATEGORY_SYSTEM, code, display: CATEGORY_LABEL[code] }] }];
}

export interface FlagDialogProps {
  patientId: string;
  editing: FlagDto | null;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

export function FlagDialog({ patientId, editing, open, onClose, onExited }: FlagDialogProps) {
  const { once } = useSubmitOnce();
  const form = useAppForm({
    defaultValues: {
      label: editing?.code.text ?? editing?.code.display ?? "",
      category: editing?.category?.[0]?.code ?? "safety",
    },
    validators: { onDynamic: flagFormSchema },
    onSubmit: ({ value }) =>
      once((entityId) => {
        if (editing) {
          updateFlag({
            flagId: editing.flagId,
            code: { text: value.label.trim() },
            category: categoryConcept(value.category),
          });
        } else if (patientId) {
          raiseFlag({
            id: entityId,
            patient: { resourceType: "Patient", id: asPatientId(patientId) },
            code: { text: value.label.trim() },
            category: categoryConcept(value.category),
          });
        }
        onClose();
      }),
    onSubmitInvalid: focusFirstInvalid,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => !next && onExited()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogTitle>{editing ? "Edit Flag" : "Raise Flag"}</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="mt-2 space-y-4"
        >
          <form.AppField name="label">
            {(field) => <field.InputField label="Alert" placeholder="e.g. Passive SI — screened" />}
          </form.AppField>
          <form.AppField name="category">
            {(field) => <field.OptionGroupField size="sm" label="Category" options={[...CATEGORY_OPTIONS]} />}
          </form.AppField>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton disabled={!editing && !patientId}>{editing ? "Save" : "Raise Flag"}</form.SubmitButton>
            </form.AppForm>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
